/**
 * `TasklistView` — the shared Pendências surface, used BOTH as the top-level
 * pane and (with a fixed `caseId`) embedded in a case workspace. The case never
 * owns a second store: it is a filtered view of the one list.
 *
 * ## Layout (redesign plan T1)
 *
 * MASTER-DETAIL at `lg` and up: the list keeps its column on the left and the
 * selected task opens beside it. The ledger timeline is the reason the detail
 * exists, and the previous full-swap made reaching it cost the lawyer their
 * place in the list. Below `lg` it falls back to the original swap (the list
 * hides, the detail fills), which is still the right shape in a narrow column.
 * The case context pane composes `TaskRow`/`TaskDetail` directly rather than
 * this view, so it keeps its own compact presentation.
 *
 * ## Ordering
 *
 * Rows render in the server's CANONICAL order. The client only ever GROUPS —
 * by section (Vencidas / Vence em breve / …), by case, or by deadline horizon
 * — and never re-sorts. All three view modes come from `@lexiun/tasklist-core`,
 * the same package LexPortal reads, so the two planes cannot drift.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Loader2, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { useCases } from "@/hooks/useCases";
import { buildCaseLabelIndex, resolveCaseLabel } from "@/components/team-of-agents/case-label";
import { useTasklist, useTaskDetail } from "@/hooks/useTasklist";
import {
  DEADLINE_BUCKETS,
  groupByCase,
  groupByDeadline,
  groupTasks,
  localDatePlus,
  localToday,
  NO_CASE_BUCKET,
  type TasklistViewMode,
} from "@lexiun/tasklist-core";
import type {
  TaskPriority,
  TaskStatus,
  TasklistFilters,
} from "@/api/lx-tasklist";
import { TaskRow } from "./TaskRow";
import { TaskDetail } from "./TaskDetail";
import { AddTaskDialog } from "./AddTaskDialog";

const STATUSES: TaskStatus[] = ["open", "waiting", "done", "dropped"];
const PRIORITIES: TaskPriority[] = ["urgent", "high", "normal", "low"];
const VIEW_MODES: TasklistViewMode[] = ["list", "case", "deadline"];

export function TasklistView({
  caseId,
  /**
   * Narrow HOST (the side pane, ~840px). The master-detail split keys off the
   * `lg:` breakpoint, which measures the VIEWPORT — so on a wide monitor it
   * would happily split an 840px pane into a 520px list and a 317px detail,
   * squeezing the ledger the detail exists to show. A container query would be
   * the elegant fix; this prop is the honest one: the host knows it is narrow,
   * so it says so and gets the single-column swap.
   */
  compact = false,
}: {
  caseId?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | null>(null);
  const [dueFilter, setDueFilter] = useState<"overdue" | "soon" | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [mode, setMode] = useState<TasklistViewMode>("list");

  const filters: TasklistFilters = useMemo(
    () => ({
      ...(caseId ? { caseId } : {}),
      // One-element arrays: the wire takes Status[]/Priority[] even though
      // this UI is single-select (see TasklistFilters).
      ...(statusFilter ? { status: [statusFilter] } : {}),
      ...(priorityFilter ? { priority: [priorityFilter] } : {}),
      ...(dueFilter ? { due: dueFilter } : {}),
      includeArchived,
    }),
    [caseId, statusFilter, priorityFilter, dueFilter, includeArchived],
  );

  const { tasks, isLoading, error, refetch } = useTasklist(filters);
  const { data: caseList } = useCases();
  const caseNames = useMemo(() => buildCaseLabelIndex(caseList), [caseList]);

  const today = localToday();
  const soonCutoff = localDatePlus(2);
  const groups = useMemo(
    () => groupTasks(tasks, today, soonCutoff),
    [tasks, today, soonCutoff],
  );
  const caseBuckets = useMemo(
    () => (mode === "case" ? groupByCase(tasks) : []),
    [mode, tasks],
  );
  const deadlineBuckets = useMemo(
    () => (mode === "deadline" ? groupByDeadline(tasks, today, soonCutoff) : null),
    [mode, tasks, today, soonCutoff],
  );

  const detail = useTaskDetail(selectedId);
  // In the case pane the case IS the filter, so grouping by case is noise.
  const modes = caseId ? VIEW_MODES.filter((m) => m !== "case") : VIEW_MODES;

  const detailNode = detail.task ? (
    <TaskDetail
      task={detail.task}
      history={detail.history}
      historyTotal={detail.historyTotal}
      hasMoreHistory={detail.hasMoreHistory}
      isLoadingMore={detail.isLoadingMore}
      onLoadOlder={() => void detail.loadOlder()}
      onBack={() => setSelectedId(null)}
      onOpenTask={(id) => setSelectedId(id)}
      onChanged={() => {
        void detail.refetch();
        void refetch();
      }}
    />
  ) : null;

  // Narrow host: the detail takes the whole pane and `onBack` returns.
  if (compact && selectedId && detailNode) return detailNode;

  const renderRows = (rows: typeof tasks) =>
    rows.map((task) => (
      <TaskRow
        key={task.id}
        task={task}
        today={today}
        caseNames={caseNames}
        selected={selectedId === task.id}
        onOpen={setSelectedId}
      />
    ));

  const renderGroup = (key: string, label: string | null, rows: typeof tasks) =>
    rows.length > 0 && (
      <div key={key} data-testid={`task-group-${key}`}>
        {label && <SectionHeader>{label}</SectionHeader>}
        <ul>{renderRows(rows)}</ul>
      </div>
    );

  const hasAny = tasks.length > 0;

  const listBody = (
    <>
      {isLoading && tasks.length === 0 && (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {error && (
        <div
          data-testid="tasklist-error"
          role="alert"
          className="m-3 rounded border border-red-200 bg-red-50 p-3 text-[13px] text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {t("tasklist.loadFailed", { message: error })}
        </div>
      )}

      {!isLoading && !error && !hasAny && (
        <div
          data-testid="tasklist-empty"
          className="flex flex-col items-center justify-center py-16 text-center text-[13px] text-slate-400 dark:text-slate-500"
        >
          <p className="text-2xl">✓</p>
          <p className="mt-2">{t("tasklist.emptyTitle")}</p>
          <p className="mt-0.5 text-[12px]">{t("tasklist.emptyHint")}</p>
        </div>
      )}

      {/* ── mode: list (canonical, sectioned) ───────────────────────── */}
      {mode === "list" && (
        <>
          {renderGroup("overdue", t("tasklist.groupOverdue"), groups.overdue)}
          {renderGroup("soon", t("tasklist.groupSoon"), groups.soon)}
          {renderGroup("rest", null, groups.rest)}
          {groups.terminal.length > 0 && (
            <div data-testid="task-group-terminal">
              <button
                type="button"
                data-testid="tasklist-terminal-toggle"
                onClick={() => setShowTerminal((v) => !v)}
                className="flex w-full items-center gap-1.5 px-5 pb-1 pt-5 text-[12px] font-medium uppercase tracking-wide text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                {showTerminal ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {t("tasklist.groupDone", { count: groups.terminal.length })}
              </button>
              {showTerminal && <ul>{renderRows(groups.terminal)}</ul>}
            </div>
          )}
        </>
      )}

      {/* ── mode: case ──────────────────────────────────────────────── */}
      {mode === "case" &&
        caseBuckets.map((b) => (
          <div key={b.key} data-testid={`task-case-group-${b.key}`}>
            <SectionHeader plain>
              {b.key === NO_CASE_BUCKET ? (
                t("tasklist.noCaseBucket")
              ) : (
                <CaseBucketLabel caseId={b.caseId!} caseNames={caseNames} />
              )}
              <span className="ml-1.5 font-normal text-slate-300 dark:text-slate-600">
                {b.tasks.length}
              </span>
            </SectionHeader>
            <ul>{renderRows(b.tasks)}</ul>
          </div>
        ))}

      {/* ── mode: deadline ──────────────────────────────────────────── */}
      {mode === "deadline" &&
        deadlineBuckets &&
        DEADLINE_BUCKETS.map((bucket) =>
          renderGroup(
            `deadline-${bucket}`,
            t(`tasklist.deadlineBucket.${bucket}`),
            deadlineBuckets[bucket],
          ),
        )}
    </>
  );

  return (
    <div className="flex h-full flex-col" data-testid="tasklist-view">
      {/* Filter + view-mode bar. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-cream-200/60 py-3.5 dark:border-coffee-800/60">
        {modes.length > 1 && (
          <div
            role="tablist"
            aria-label={t("tasklist.viewMode")}
            className="mr-2 flex items-center rounded-md border border-cream-300 dark:border-coffee-700"
          >
            {modes.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                data-testid={`view-mode-${m}`}
                onClick={() => setMode(m)}
                className={cn(
                  "px-3 py-1 text-[13px] transition-colors first:rounded-l-md last:rounded-r-md",
                  mode === m
                    ? "bg-cream-200/70 font-medium text-slate-700 dark:bg-coffee-800 dark:text-slate-200"
                    : "text-slate-500 hover:bg-cream-100 dark:text-slate-400 dark:hover:bg-coffee-800/50",
                )}
              >
                {t(`tasklist.mode.${m}`)}
              </button>
            ))}
          </div>
        )}
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            active={statusFilter === s}
            onClick={() => setStatusFilter((v) => (v === s ? null : s))}
            testId={`filter-status-${s}`}
          >
            {t(`tasklist.status.${s}`)}
          </FilterChip>
        ))}
        <span className="mx-1.5 h-4 w-px bg-cream-300 dark:bg-coffee-700" />
        <FilterChip
          active={dueFilter === "overdue"}
          onClick={() => setDueFilter((v) => (v === "overdue" ? null : "overdue"))}
          testId="filter-due-overdue"
        >
          {t("tasklist.filterOverdue")}
        </FilterChip>
        <FilterChip
          active={dueFilter === "soon"}
          onClick={() => setDueFilter((v) => (v === "soon" ? null : "soon"))}
          testId="filter-due-soon"
        >
          {t("tasklist.filterSoon")}
        </FilterChip>
        <select
          data-testid="filter-priority"
          value={priorityFilter ?? ""}
          onChange={(e) =>
            setPriorityFilter((e.target.value || null) as TaskPriority | null)
          }
          className="rounded border border-cream-300 bg-white px-2 py-1 text-[13px] dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-200"
        >
          <option value="">{t("tasklist.anyPriority")}</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`tasklist.priority.${p}`)}
            </option>
          ))}
        </select>
        <label className="ml-auto flex items-center gap-1.5 text-[13px] text-slate-500 dark:text-slate-400">
          <input
            type="checkbox"
            data-testid="filter-archived"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          {t("tasklist.showArchived")}
        </label>
        <button
          type="button"
          data-testid="tasklist-add"
          onClick={() => setAddOpen(true)}
          className="rounded-md border border-cream-300 px-3 py-1 text-[13px] font-normal text-slate-600 hover:bg-cream-100 dark:border-coffee-700 dark:text-slate-300 dark:hover:bg-coffee-800"
        >
          <Plus size={12} className="mr-0.5 inline" aria-hidden />
          {t("tasklist.add")}
        </button>
      </div>

      {compact ? (
        <div className="min-h-0 flex-1 overflow-y-auto">{listBody}</div>
      ) : (
      <div className="flex min-h-0 flex-1">
          {/* Master. Below lg the detail overlays it entirely. */}
          <div
            data-testid="tasklist-list-column"
            className={cn(
              "min-w-0 flex-1 overflow-y-auto lg:min-w-[380px] lg:max-w-[520px] lg:flex-none lg:border-r lg:border-cream-200/60 lg:dark:border-coffee-800/60",
              selectedId && "hidden lg:block",
            )}
          >
            {listBody}
          </div>
          {/* Detail. */}
          <div
            data-testid="tasklist-detail-column"
            className={cn(
              "min-w-0 flex-1 overflow-y-auto",
              !selectedId && "hidden lg:block",
            )}
          >
            {detailNode ?? <DetailPlaceholder />}
        </div>
      </div>
      )}

      {addOpen && (
        <AddTaskDialog
          initialCaseId={caseId}
          onClose={() => setAddOpen(false)}
          onCreated={() => void refetch()}
        />
      )}
    </div>
  );
}

function SectionHeader({
  children,
  /** `true` for proper nouns (case names), which must not be upper-cased. */
  plain = false,
}: {
  children: React.ReactNode;
  plain?: boolean;
}) {
  return (
    <div
      className={cn(
        "px-5 pb-1 pt-5 text-[12px] font-medium tracking-wide text-slate-400 first:pt-3 dark:text-slate-500",
        !plain && "uppercase",
      )}
    >
      {children}
    </div>
  );
}

/**
 * A case bucket's heading. When the registry cannot resolve the ref — an
 * archived or deleted case — fall back to the marker PLUS a short suffix of the
 * ref itself. Without the suffix two different dead cases both render "caso
 * arquivado" and become one indistinguishable heading, which is worse than
 * showing a raw id: it silently merges two matters in the reader's mind.
 */
function CaseBucketLabel({
  caseId,
  caseNames,
}: {
  caseId: string;
  caseNames: Map<string, string>;
}) {
  const { t } = useTranslation();
  const label = resolveCaseLabel(caseId, caseNames);
  if (label) return <>{label}</>;
  return (
    // The trailing `· {caseId.split("-").pop()}` fragment and the full-id
    // `title` were removed 2026-07-27 — no raw entity id, or fragment of one,
    // may reach user-facing copy. The suffix was there to disambiguate two
    // archived buckets, but base36 entropy ("rign3u") maps to nothing the lawyer
    // can recognise, so it bought noise rather than distinguishability. If
    // colliding archived buckets become a real problem, the fix is resolving
    // ARCHIVED case names (a data gap), not showing the id.
    <span data-testid={`task-case-unresolved-${caseId}`}>{t("tasklist.archivedCase")}</span>
  );
}

function DetailPlaceholder() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="tasklist-detail-empty"
      className="flex h-full flex-col items-center justify-center px-6 text-center text-[13px] text-slate-400 dark:text-slate-500"
    >
      <p>{t("tasklist.detailPlaceholder")}</p>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded px-2.5 py-1 text-[13px] font-normal transition-colors",
        active
          ? "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300"
          : "text-slate-500 hover:bg-cream-100 dark:text-slate-400 dark:hover:bg-coffee-800",
      )}
    >
      {children}
    </button>
  );
}
